import { StrictMode } from 'react';
import { createRoot } from 'react-dom/client';
import { BrowserRouter } from 'react-router-dom';
import { PublicClientApplication, EventType } from '@azure/msal-browser';
import { MsalProvider } from '@azure/msal-react';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { msalConfig } from './authConfig';
import { AuthProvider } from './contexts/AuthContext';
import { ThemeProvider } from './contexts/ThemeContext';
import { ToastProvider } from './components/Feedback';
import { ApiError } from './lib/api';
import { ErrorBoundary, FatalError } from './components/ErrorBoundary';
import { App } from './App';
import './index.css';

const msalInstance = new PublicClientApplication(msalConfig);

const queryClient = new QueryClient({
    defaultOptions: {
        queries: {
            staleTime: 30_000,
            refetchOnWindowFocus: false,
            retry: (failureCount, error) => {
                // Réessayer sur un 401 ou un 403 ne sert à rien : la réponse ne
                // changera pas tant que les droits n'auront pas changé.
                if (error instanceof ApiError && error.status < 500) return false;
                return failureCount < 2;
            },
        },
    },
});

async function bootstrap() {
    await msalInstance.initialize();

    // Reprend la session existante après un rechargement de page.
    const accounts = msalInstance.getAllAccounts();
    if (accounts.length > 0) {
        msalInstance.setActiveAccount(accounts[0]);
    }

    msalInstance.addEventCallback((event) => {
        if (
            (event.eventType === EventType.LOGIN_SUCCESS ||
                event.eventType === EventType.ACQUIRE_TOKEN_SUCCESS) &&
            event.payload &&
            'account' in event.payload &&
            event.payload.account
        ) {
            msalInstance.setActiveAccount(event.payload.account);
        }
    });

    await msalInstance.handleRedirectPromise();

    createRoot(document.getElementById('root')!).render(
        <StrictMode>
            <ErrorBoundary>
            <MsalProvider instance={msalInstance}>
                <QueryClientProvider client={queryClient}>
                    <ThemeProvider>
                        <ToastProvider>
                            <BrowserRouter>
                                <AuthProvider>
                                    <App />
                                </AuthProvider>
                            </BrowserRouter>
                        </ToastProvider>
                    </ThemeProvider>
                </QueryClientProvider>
            </MsalProvider>
            </ErrorBoundary>
        </StrictMode>
    );
}

/**
 * L'amorcage doit rattraper ses propres echecs.
 *
 * `initialize()` et surtout `handleRedirectPromise()` peuvent rejeter : une URI
 * de redirection qui ne correspond pas, une portee absente de l'App
 * Registration, un jeton refuse. Sans ce `catch`, la promesse partait en rejet
 * silencieux, `render()` n'etait jamais atteint et le `<div id="root">` restait
 * vide. Resultat, une page blanche apparaissant precisement au retour de
 * l'authentification, sans message ni trace pour en identifier la cause.
 */
void bootstrap().catch((error) => {
    console.error("Echec de l'amorcage de l'application", error);
    createRoot(document.getElementById('root')!).render(
        <StrictMode>
            <FatalError title="La session n'a pas pu s'ouvrir" error={error} />
        </StrictMode>
    );
});
